/**
 * Records the conversation as it happens, in the session-file shape
 * (session-file.ts), for whichever provider loop is running.
 *
 * AgentService feeds it at three points every loop shares: the user's message,
 * the streamed text, and each tool call (`record`). A turn is committed when it
 * ends, interrupted or not, and listeners (the editor-content store) write it
 * into the scene.
 *
 * A conversation belongs to the scene it was recorded in, named by its
 * document root: the first turn under another root starts a new file, so a
 * scene's editor content never holds another scene's history. The model's own
 * history is AgentService's, and is not touched; a restored conversation
 * reaches it only as a quoted user-role transcript ({@link peekPreamble}).
 */

import type { TraceScene } from 'trace-viewer/scene';
import {
    capToolInput, MAX_TOOL_OUTPUT_CHARS, newSessionFile, toSummaryText, truncate,
    type AiSessionFile, type AiSessionTurn,
} from './session-file';

/** Inline images never reach the file (spec §2.10): they are what makes histories huge. */
const DATA_URL = /data:[\w.+-]+\/[\w.+-]+;base64,[A-Za-z0-9+/=]+/g;

export function toolOutputText(result: string): string {
    return truncate(result.replace(DATA_URL, '[image]'), MAX_TOOL_OUTPUT_CHARS);
}

export class SessionRecorder {
    private file: AiSessionFile | null = null;
    private turn: AiSessionTurn | null = null;
    /** The document root `file` was recorded under. */
    private owner: object | null = null;
    private readonly listeners = new Set<(file: AiSessionFile, owner: object | null) => void>();
    private readonly resumeListeners = new Set<(file: AiSessionFile) => void>();
    /** The restored conversation as quoted text, until the next message carries it. */
    private preamble: string | null = null;

    /** The conversation so far, or null before the first turn. */
    get current(): AiSessionFile | null {
        return this.file;
    }

    /**
     * Called with the whole file each time a turn is committed, and the
     * document root it was recorded under: the scene it belongs to, which may
     * no longer be the one open.
     */
    onCommit(listener: (file: AiSessionFile, owner: object | null) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /** Whether the conversation under way belongs to the scene under `owner`. */
    isUnderWay(owner: object | null): boolean {
        return !!this.file?.turns.length && this.owner === owner;
    }

    /** Start a new conversation at the next turn. */
    reset(): void {
        this.file = null;
        this.turn = null;
        this.preamble = null;
    }

    /**
     * Continue `file` in the scene under `owner`: the next turn there is
     * appended to it, and the model is given it, quoted, with that turn.
     */
    resume(file: AiSessionFile, owner: object | null): void {
        const preamble = toSummaryText(file);
        this.file = file;
        this.turn = null;
        this.owner = owner;
        this.preamble = preamble;
        for (const listener of this.resumeListeners) listener(file);
    }

    /** Called when a conversation is picked up from a file, so the panel can show it. */
    onResume(listener: (file: AiSessionFile) => void): () => void {
        this.resumeListeners.add(listener);
        return () => this.resumeListeners.delete(listener);
    }

    /** The restored transcript: the user-role text to put ahead of the next message, until {@link dropPreamble}. */
    peekPreamble(): string | null {
        return this.preamble;
    }

    /** The next message carried the transcript into the model's history. */
    dropPreamble(): void {
        this.preamble = null;
    }

    beginTurn(user: string, attachments: { name: string; mimeType: string }[], owner: object | null): void {
        if (owner !== this.owner) this.file = null;
        this.owner = owner;
        this.file ??= newSessionFile('sandbox');
        this.turn = {
            at: new Date().toISOString(),
            user,
            ...(attachments.length ? { attachments } : {}),
            steps: [],
        };
    }

    /**
     * Pick a paused turn back up (Continue): its steps carry on in the same
     * record. If the scene changed since the pause, the continuation starts a
     * turn of its own, with no user message, in that scene's file.
     */
    continueTurn(owner: object | null): void {
        const last = owner === this.owner ? this.file?.turns.pop() : undefined;
        if (last) this.turn = last;
        else this.beginTurn('', [], owner);
    }

    text(chunk: string): void {
        const steps = this.turn?.steps;
        if (!steps || !chunk) return;
        const last = steps.at(-1);
        if (last && 'text' in last) last.text += chunk;
        else steps.push({ text: chunk });
    }

    record(tool: string, input: unknown, output: string, nodes: string[], error: boolean): void {
        this.turn?.steps.push({
            tool,
            input: capToolInput(input),
            output: toolOutputText(output),
            ...(nodes.length ? { nodes } : {}),
            ...(error ? { error } : {}),
        });
    }

    /** Commit the turn in progress; a turn with nothing in it is still the user's words. */
    endTurn(): void {
        if (!this.file || !this.turn) return;
        this.file.turns.push(this.turn);
        this.file.updatedAt = new Date().toISOString();
        this.turn = null;
        for (const listener of this.listeners) listener(this.file, this.owner);
    }
}

function sceneUids(scene: TraceScene | null): Set<string> {
    return new Set(scene?.getSceneObjects().flatMap(o => (o.uid ? [o.uid] : [])) ?? []);
}

/**
 * Run a tool call and record it with the objects it created.
 *
 * "Created" is every uid that appeared while the call ran, so attribution
 * assumes the call is the only writer in that window: tool calls run one at a
 * time, but an object the user adds by hand during a slow call is attributed
 * to it too.
 */
export async function recordToolCall(
    recorder: SessionRecorder,
    scene: TraceScene | null,
    name: string,
    input: Record<string, unknown>,
    run: () => Promise<string>,
): Promise<string> {
    const before = sceneUids(scene);
    const created = () => [...sceneUids(scene)].filter(uid => !before.has(uid));
    try {
        const result = await run();
        recorder.record(name, input, result, created(), result.startsWith('Error'));
        return result;
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        recorder.record(name, input, `Error: ${message}`, created(), true);
        throw err;
    }
}
